import { createFileRoute, Link, Navigate, Outlet, useParams } from '@tanstack/react-router';
import { useGetAgencyMe } from '@/api/generated/agency/agency';
import { AgencyPageState } from '@/features/agency';

export const Route = createFileRoute('/_auth/agency/members')({
  component: AgencyMembersLayout,
});

/**
 * Members section layout. Enforces the admin gate once so every member
 * sub-route (list, add, detail) inherits it, then renders the active child.
 * A non-admin opening their own detail URL (an old link) lands on
 * `/agency/profile` instead of the gate — that route only redirects admins,
 * so the two can't loop.
 */
function AgencyMembersLayout() {
  const agencyMeQuery = useGetAgencyMe();
  const me = agencyMeQuery.data?.data;
  const isAdmin = me?.role === 'ADMIN';
  const { memberId } = useParams({ strict: false }) as { memberId?: string };

  if (!agencyMeQuery.isLoading && !isAdmin && me && memberId === me.id) {
    return <Navigate to="/agency/profile" replace />;
  }

  if (!agencyMeQuery.isLoading && !isAdmin) {
    return (
      <section className="rounded-[var(--r-2xl)] border border-clovie-border bg-clovie-surface shadow-soft-lg">
        <AgencyPageState
          title="Admin Access Required"
          description="Only agency administrators can manage members and salon assignments."
        />
        <div className="border-t border-clovie-border-soft p-4 text-center">
          <Link
            to="/agency/salons"
            className="inline-flex h-11 items-center rounded-[var(--r-pill)] bg-clovie-primary px-5 text-[16px] font-semibold text-white hover:bg-clovie-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clovie-brand focus-visible:ring-offset-2"
          >
            Back to Businesses
          </Link>
        </div>
      </section>
    );
  }

  return <Outlet />;
}
